import React, { useEffect, useState } from 'react';
import { ConfigProvider } from 'antd';
import { shallowEqual, useSelector } from 'react-redux';
import useBodyClass from './helpers/useBodyClass';
import AppLocale from './configs/app-locale';
import i18n from './configs/i18next';
import useDocumentTitle from './helpers/projectTitle';
import { PROJECT_NAME } from './configs/app-global';
import { useTranslation } from 'react-i18next';

function antdLocaleForI18n() {
  const code = (i18n.language || 'en').split('-')[0];
  return AppLocale[code] || AppLocale.en;
}

export default function Providers({ children }) {
  const { theme } = useSelector((state) => state.theme, shallowEqual);
  const [, setLangTick] = useState(0);
  useBodyClass(`dir-${theme.direction}`);
  const { settings } = useSelector((state) => state.globalSettings);
  const activeMenu = useSelector((state) => state.menu.activeMenu);
  const { t } = useTranslation();

  // "Products · Dashboard — Kamil Store". The section leads because a pinned
  // tab shows only its opening characters, and the shop name is the part that
  // is identical on every one of them.
  const shopName = settings.title || PROJECT_NAME;
  const sectionKey = activeMenu?.name;

  /**
   * A detail route names itself after the record it opened — a product edit
   * tab is called "26ab47d0-678d-45be-…", which is a UUID in the browser tab
   * and tells nobody anything. Fall back to the section it belongs to.
   */
  const looksLikeId = (value) =>
    typeof value === 'string' &&
    (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(value) || /^[0-9a-f]{24}$/i.test(value));

  const resolvedKey = looksLikeId(sectionKey)
    ? (activeMenu?.url || '').split('/').filter(Boolean)[0] || ''
    : sectionKey;

  const section =
    resolvedKey && resolvedKey !== 'dashboard'
      ? t(resolvedKey, { defaultValue: '' }).trim()
      : '';
  useDocumentTitle(
    section ? `${section} · Dashboard — ${shopName}` : `Dashboard — ${shopName}`,
  );

  useEffect(() => {
    const onLang = () => setLangTick((n) => n + 1);
    i18n.on('languageChanged', onLang);
    return () => i18n.off('languageChanged', onLang);
  }, []);

  return (
    <ConfigProvider
      direction={theme.direction}
      locale={antdLocaleForI18n()}
    >
      {children}
    </ConfigProvider>
  );
}
